import { useState, useEffect, useRef } from 'react';
import { processMultiplePdfs, getJobStatus } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * PDF 분석 결과를 보여주고 승인을 받는 모달 컴포넌트
 */
function PdfAnalysisModal({ analysisResult, onClose, onSuccess }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false); // 모달 최소화 상태
  const [processingStatus, setProcessingStatus] = useState(''); // 처리 상태 메시지
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 }); // 진행률 정보
  const { user } = useAuth(); // 사용자 정보 (userId 추출용)
  const progressIntervalRef = useRef(null); // 진행률 조회 인터벌 참조
  const finalCheckTimeoutRef = useRef(null); // 최종 상태 확인 타임아웃 참조

  const { new: newProperties, updated: updatedProperties, skipped: skippedProperties, completed: completedProperties, _analysisData } = analysisResult;
  
  // 상권 정보 추출
  const default상권 = _analysisData?.상권 || '';
  
  // 진행률 조회 인터벌 및 타임아웃 정리
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (finalCheckTimeoutRef.current) {
        clearTimeout(finalCheckTimeoutRef.current);
      }
    };
  }, []);

  const handleApprove = async () => {
    if (!_analysisData) {
      setError('분석 데이터가 없습니다.');
      return;
    }

    // 처리 시작 확인
    if (!window.confirm('PDF 처리를 시작하시겠습니까?\n\n처리 중에는 창을 최소화할 수 있습니다.')) {
      return; // 사용자가 취소한 경우
    }

    setIsProcessing(true);
    setError(null);
    setProcessingStatus('PDF 처리 시작...');
    setProgress({ current: 0, total: _analysisData.pdfDataList?.length || 0, percentage: 0 });

    console.log('📋 PDF 처리 시작:', {
      파일수: _analysisData.pdfDataList?.length || 0,
      상권: default상권
    });
    
    // 파일별 상권 매핑이 있으면 사용, 없으면 기본 상권 사용
    setProcessingStatus('PDF 파일 처리 중...');
    
    // jobId를 미리 생성하여 진행률 조회를 즉시 시작
    const userId = user?.uid || null;
    const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    
    // 진행률 조회 인터벌 즉시 시작 (서버 응답을 기다리지 않음)
    if (userId) {
      progressIntervalRef.current = setInterval(async () => {
        try {
          const jobStatus = await getJobStatus(jobId);
          if (jobStatus) {
            const percentage = jobStatus.totalFiles > 0 
              ? Math.round((jobStatus.completedFiles / jobStatus.totalFiles) * 100)
              : 0;
            setProgress({
              current: jobStatus.completedFiles || 0,
              total: jobStatus.totalFiles || 0,
              percentage
            });
            setProcessingStatus(`처리 중... (${jobStatus.completedFiles || 0}/${jobStatus.totalFiles || 0} 파일)`);
            
            // 완료되면 인터벌 정리 및 성공 처리
            if (jobStatus.status === 'completed') {
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
              // finalCheckTimeout이 있으면 취소
              if (finalCheckTimeoutRef.current) {
                clearTimeout(finalCheckTimeoutRef.current);
                finalCheckTimeoutRef.current = null;
              }
              
              // 요청이 실패했지만 백엔드에서 완료된 경우 성공 처리
              setProcessingStatus('처리 완료!');
              setError(null);
              setProgress({
                current: jobStatus.completedFiles || 0,
                total: jobStatus.totalFiles || 0,
                percentage: 100
              });
              
              if (onSuccess) {
                onSuccess({ jobId, ...jobStatus });
              }
              
              setIsMinimized(false);
              onClose();
            }
          }
        } catch (error) {
          // 작업이 아직 시작되지 않았을 수 있으므로 에러는 무시
          console.warn('진행률 조회 실패 (작업이 아직 시작되지 않았을 수 있음):', error.message);
        }
      }, 2000); // 2초마다 조회
    }
    
    // processMultiplePdfs 호출 (jobId를 전달하여 서버에서 사용)
    // 요청이 실패해도 백엔드에서 작업이 진행 중일 수 있으므로 진행률 조회는 계속 유지
    let requestError = null;
    
    try {
        const result = await processMultiplePdfs(_analysisData, default상권, null, userId, jobId);
        
        console.log('✅ PDF 처리 완료:', result);
        
        // 백업 상태 확인 및 사용자에게 알림
        if (result?.backup) {
          const backup = result.backup;
          if (backup.failed) {
            console.warn('⚠️ 백업 생성 실패:', backup.reason);
            setError(`PDF 처리는 완료되었지만 백업 생성에 실패했습니다: ${backup.reason || '알 수 없는 오류'}`);
          } else if (backup.skipped) {
            console.warn('⚠️ 백업 생성 건너뜀:', backup.reason);
            // 백업이 건너뛰어진 경우는 경고만 표시 (에러는 아님)
            if (backup.reason && backup.reason.includes('userId가 없어')) {
              console.warn('백업이 생성되지 않았습니다. 로그인 상태를 확인해주세요.');
            }
          } else if (backup.created) {
            console.log('✅ 백업 생성 완료:', backup.backupId);
          }
        }
        
        setProcessingStatus('처리 완료!');
        
        // 진행률 조회 인터벌 정리
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        
        // 최종 진행률 업데이트
        if (result?.jobId && userId) {
          const jobStatus = await getJobStatus(result.jobId);
          if (jobStatus) {
            setProgress({
              current: jobStatus.completedFiles || 0,
              total: jobStatus.totalFiles || 0,
              percentage: 100
            });
          }
        }
        
        if (onSuccess) {
          onSuccess(result);
        }
        
        // 성공 후 모달 닫기
        setIsMinimized(false);
        onClose();
      } catch (err) {
        // processMultiplePdfs 요청 실패
        console.error('❌ PDF 처리 요청 실패:', err);
        requestError = err;
        
        // 요청이 실패했지만 백엔드에서 작업이 진행 중일 수 있음
        // 진행률 조회는 계속 유지하고, 일정 시간 후 최종 확인
        setError('PDF 처리 요청이 실패했습니다. 백엔드에서 작업이 진행 중일 수 있으니 진행률을 확인해주세요.');
        setProcessingStatus('요청 실패 - 진행률 확인 중...');
        
        // 60초 후 최종 상태 확인 (백엔드 작업이 완료될 시간을 고려)
        // 진행률 조회 인터벌에서 완료를 감지하지 못한 경우를 대비
        finalCheckTimeoutRef.current = setTimeout(async () => {
          try {
            const finalStatus = await getJobStatus(jobId);
            if (finalStatus && finalStatus.status === 'completed') {
              // 백엔드에서 작업이 완료됨
              setProcessingStatus('처리 완료!');
              setError(null);
              setProgress({
                current: finalStatus.completedFiles || 0,
                total: finalStatus.totalFiles || 0,
                percentage: 100
              });
              
              if (onSuccess) {
                onSuccess({ jobId, ...finalStatus });
              }
              
              // 진행률 조회 인터벌 정리
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
              
              setIsMinimized(false);
              onClose();
            } else {
              // 백엔드에서도 작업이 완료되지 않음
              const errorMessage = requestError?.message || 'PDF 처리 중 오류가 발생했습니다.';
              setError(`PDF 처리 요청이 실패했습니다: ${errorMessage.split('\n').join(' ')}`);
              setProcessingStatus('처리 실패');
              
              // 진행률 조회 인터벌 정리
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
              
              setIsProcessing(false);
              setIsMinimized(false);
            }
          } catch (statusError) {
            console.error('최종 상태 확인 실패:', statusError);
            const errorMessage = requestError?.message || 'PDF 처리 중 오류가 발생했습니다.';
            setError(`PDF 처리 요청이 실패했습니다: ${errorMessage.split('\n').join(' ')}`);
            setProcessingStatus('처리 실패');
            
            // 진행률 조회 인터벌 정리
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            
            setIsProcessing(false);
            setIsMinimized(false);
          }
        }, 60000); // 60초 후 확인
        
        // 진행률 조회는 이미 실행 중이므로 계속 유지
        // 진행률 조회 인터벌에서 완료를 감지하면 자동으로 처리됨
      }
  };

  // 최소화된 상태 UI
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 bg-white shadow-2xl rounded-lg border-2 border-orange-500 z-50 cursor-pointer hover:shadow-3xl transition-shadow"
        onClick={() => setIsMinimized(false)}
        style={{ minWidth: '200px' }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-800">
              {isProcessing ? 'PDF 처리 진행 중' : 'PDF 분석 결과'}
            </h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isProcessing) {
                  if (window.confirm('PDF 처리가 진행 중입니다. 정말 닫으시겠습니까?\n\n작업이 중단될 수 있습니다.')) {
                    setIsProcessing(false);
                    setIsMinimized(false);
                    onClose();
                  }
                } else {
                  onClose();
                }
              }}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label="닫기"
            >
              ×
            </button>
          </div>
          {isProcessing ? (
            <div className="mb-2">
              <div className="flex items-center gap-2 mb-1">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                <span className="text-xs text-gray-700 font-medium">{processingStatus || '처리 중...'}</span>
              </div>
              {progress.total > 0 && (
                <div className="mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-orange-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${progress.percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    {progress.current} / {progress.total} ({progress.percentage}%)
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-600">
              신규: {newProperties.length} | 변경: {updatedProperties.length} | 삭제: {completedProperties.length}
            </div>
          )}
          <p className="text-xs text-gray-500 text-center mt-2">클릭하여 확대</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">PDF 분석 결과 확인</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(true)}
                className="text-gray-400 hover:text-gray-600 text-lg px-2 py-1 rounded hover:bg-gray-100"
                disabled={isProcessing}
                aria-label="최소화"
                title="최소화"
              >
                ─
              </button>
              <button
                onClick={() => {
                  if (isProcessing) {
                    if (window.confirm('PDF 처리가 진행 중입니다. 정말 닫으시겠습니까?\n\n작업이 중단될 수 있습니다.')) {
                      setIsProcessing(false);
                      setIsMinimized(false);
                      onClose();
                    }
                  } else {
                    onClose();
                  }
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                disabled={isProcessing}
                aria-label="닫기"
              >
                ×
              </button>
            </div>
          </div>

          {!isProcessing && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800">
                분석이 완료되었습니다. 아래 결과를 확인한 후 승인해주세요.
              </p>
            </div>
          )}

          {isProcessing && (
            <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-800">PDF 처리 진행 중</p>
                  <p className="text-xs text-blue-600 mt-1">{processingStatus || '파일을 처리하고 있습니다...'}</p>
                </div>
              </div>
              {progress.total > 0 && (
                <div className="mb-3">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${progress.percentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-blue-700">
                    <span>진행률: {progress.percentage}%</span>
                    <span>{progress.current} / {progress.total} 파일</span>
                  </div>
                </div>
              )}
              <div className="mt-3 text-xs text-blue-700 space-y-1">
                <p>• 서버 콘솔에서 상세 진행 상황을 확인할 수 있습니다.</p>
                <p>• 처리 중에는 창을 최소화할 수 있습니다.</p>
                <p>• 작업이 완료되면 자동으로 닫힙니다.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
              {error}
            </div>
          )}

          {/* 요약 정보 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <div className="text-xs text-green-600 font-medium mb-1">신규 매물</div>
              <div className="text-2xl font-bold text-green-700">{newProperties.length}</div>
            </div>
            <div className="p-3 bg-orange-50 border border-orange-200 rounded">
              <div className="text-xs text-orange-600 font-medium mb-1">변경 매물</div>
              <div className="text-2xl font-bold text-orange-700">{updatedProperties.length}</div>
            </div>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded">
              <div className="text-xs text-gray-600 font-medium mb-1">유지 매물</div>
              <div className="text-2xl font-bold text-gray-700">{skippedProperties.length}</div>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <div className="text-xs text-red-600 font-medium mb-1">삭제 매물</div>
              <div className="text-2xl font-bold text-red-700">{completedProperties.length}</div>
            </div>
          </div>

          {/* 신규 매물 리스트 */}
          {newProperties.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs">
                  {newProperties.length}
                </span>
                신규 매물
              </h3>
              <div className="border border-green-200 rounded-lg p-3 bg-green-50 max-h-48 overflow-y-auto">
                <ul className="text-xs text-gray-700 space-y-1">
                  {newProperties.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-green-600 font-medium">{index + 1}.</span>
                      <span className="truncate">{item.fileName}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 변경 매물 리스트 */}
          {updatedProperties.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-orange-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs">
                  {updatedProperties.length}
                </span>
                변경 매물
              </h3>
              <div className="border border-orange-200 rounded-lg p-3 bg-orange-50 max-h-48 overflow-y-auto">
                <ul className="text-xs text-gray-700 space-y-1">
                  {updatedProperties.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-orange-600 font-medium">{index + 1}.</span>
                      <span className="truncate">{item.fileName}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 유지 매물 리스트 */}
          {skippedProperties.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs">
                  {skippedProperties.length}
                </span>
                유지 매물 (변경 없음)
              </h3>
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                <ul className="text-xs text-gray-700 space-y-1">
                  {skippedProperties.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-gray-600 font-medium">{index + 1}.</span>
                      <span className="truncate">{item.fileName}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 삭제 매물 리스트 */}
          {completedProperties.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                  {completedProperties.length}
                </span>
                삭제될 매물 (업로드 목록에 없음)
              </h3>
              <div className="border border-red-200 rounded-lg p-3 bg-red-50 max-h-48 overflow-y-auto">
                <ul className="text-xs text-gray-700 space-y-1">
                  {completedProperties.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-red-600 font-medium">{index + 1}.</span>
                      <span className="truncate">{item.fileName}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 액션 버튼 */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
              disabled={isProcessing}
            >
              취소
            </button>
            <button
              onClick={handleApprove}
              disabled={isProcessing}
              className="px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? '처리 중...' : '승인 및 처리 시작'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PdfAnalysisModal;

